import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import analytics from 'utils/analytics'

export interface LaneError {
  rate_price?: string | null
  capacity?: string | null
}

export type LaneErrors = Record<number, LaneError>

interface State {
  carrier?: Carrier
  rfp?: RequestForProposal
  lanes: { [key: number]: Lane }
  uuid?: string
  rate_type?: string
  submitted_at?: string
  meta: {
    error: string | null
    isLoading: boolean
    laneErrors: Record<number, LaneError>
    lastSavedAt: string | null
    isSubmitting: boolean
    isUploading: boolean
    uploadError: string | null
  }
}

const initialState: State = {
  lanes: {},
  meta: {
    error: null,
    isLoading: false,
    isSubmitting: false,
    laneErrors: {},
    lastSavedAt: null,
    isUploading: false,
    uploadError: null,
  },
}

export const slice = createSlice({
  name: 'carrierProposal',
  initialState,
  reducers: {
    clearLaneSuccess(state, action: PayloadAction<LaneProposal>) {
      state.lanes[action.payload.lane].meta.isSubmitting = false
      state.meta.laneErrors[action.payload.lane] = {}
    },
    fetchRFPStart(state, action: PayloadAction<string>) {
      state.uuid = action.payload
      state.meta.isLoading = true
      state.meta.error = null
    },
    fetchRFPSuccess(state, action: PayloadAction<any>) {
      const lanes = action.payload.lanes.reduce((acc: any, curr: any) => {
        acc[curr.id] = {
          ...curr,
          meta: {
            error: null,
            isSubmitting: false,
            submitted: !!curr.lane_proposal.id,
          },
        }
        return acc
      }, {})
      state.carrier = action.payload.carrier
      state.rfp = action.payload.rfp
      state.lanes = lanes
      state.rate_type = action.payload.rate_type
      state.submitted_at = action.payload.submitted_at
      state.meta.isLoading = false

      analytics.setUser({
        email: action.payload.carrier?.email,
        rfp_id: action.payload.rfp.id,
      })
    },
    fetchRFPFailed(state, action: PayloadAction<string>) {
      state.meta.error = action.payload
      state.meta.isLoading = false
    },
    submitProposalStart(state) {
      state.meta.isSubmitting = true
    },
    submitProposalSuccess(state) {
      state.meta.isSubmitting = false
      state.submitted_at = new Date().toISOString()
    },
    submitProposalFailed(
      state,
      action: PayloadAction<LaneErrors | string | null>
    ) {
      state.meta.isSubmitting = false
      if (action.payload !== null && typeof action.payload !== 'string') {
        const errors = action.payload

        Object.values(state.lanes).forEach((lane) => {
          if (errors[lane.lane_proposal.id]) {
            state.meta.laneErrors[lane.id] = errors[lane.lane_proposal.id]
          }
        })
      } else {
        state.meta.error = action.payload
      }
    },
    submitLaneStart(state, action: PayloadAction<LaneProposal>) {
      state.lanes[action.payload.lane].meta.isSubmitting = true
      state.meta.laneErrors[action.payload.lane] = {}
    },
    submitLaneSuccess(state, action: PayloadAction<LaneProposal>) {
      state.meta.lastSavedAt = new Date().toISOString()
      state.lanes[action.payload.lane].meta.isSubmitting = false
      state.lanes[action.payload.lane].meta.submitted = true
      state.lanes[action.payload.lane].lane_proposal.rate_price =
        action.payload.rate_price
      state.lanes[action.payload.lane].lane_proposal.capacity =
        action.payload.capacity
      state.lanes[action.payload.lane].lane_proposal.id = action.payload.id
    },
    submitLaneFailed(
      state,
      action: PayloadAction<{ lane: number; error: LaneError }>
    ) {
      state.lanes[action.payload.lane].meta.isSubmitting = false
      state.meta.laneErrors[action.payload.lane] = action.payload.error
      state.meta.lastSavedAt = null
    },
    laneDeleteSuccess(state, action: PayloadAction<LaneProposal>) {
      state.meta.lastSavedAt = new Date().toISOString()
      state.lanes[action.payload.lane].meta.isSubmitting = false
      state.lanes[action.payload.lane].meta.submitted = false
      state.lanes[action.payload.lane].lane_proposal.rate_price = 0
      state.lanes[action.payload.lane].lane_proposal.capacity =
        state.lanes[action.payload.lane].volume
    },
    uploadProposalStart(state) {
      state.meta.uploadError = null
      state.meta.isUploading = true
    },
    uploadProposalSuccess(state) {
      state.meta.uploadError = null
      state.meta.isUploading = false
    },
    uploadProposalFailed(state, action: PayloadAction<string>) {
      state.meta.isUploading = false
      state.meta.uploadError = action.payload
    },
    clearUploadErrors(state) {
      state.meta.uploadError = null
    },
  },
})

export default slice.reducer
